import type { ProductEntity } from '@/entities/assortment'
import type { UseQueryResult } from '@tanstack/react-query'

import { useState } from 'react'
import { useQueries } from '@tanstack/react-query'
import { addWeeks, format } from 'date-fns'

import { coreQueries } from '@/entities/core'

import { DateFormat } from '@/shared/tools/dates'

const COMPETITIVE_FIELDS = [
	'observed_on',
	'price',
	'promo_type_id',
	'availability',
	'url',
]

const combine = ([history, competitive]: [
	UseQueryResult<
		Awaited<
			ReturnType<ReturnType<typeof coreQueries.listPriceHistory>['queryFn']>
		>
	>,
	UseQueryResult<
		Awaited<
			ReturnType<ReturnType<typeof coreQueries.listCompetitiveData>['queryFn']>
		>
	>,
]) => ({
	history: history.data,
	competitive: competitive.data,
	isLoading: history.isLoading || competitive.isLoading,
	isError: history.isError || competitive.isError,
})

/** Price history + competitive data for the last 6 weeks of one SKU line. */
export const useMarketDataQuery = (content: ProductEntity) => {
	// Fixed per mount so the query key stays stable across renders.
	const [observedAfter] = useState(() =>
		format(addWeeks(new Date(), -6), DateFormat.system)
	)
	const optimizationGroupId = content.optimization_group_id
	const params = {
		limit: 0,
		optimization_group_id: optimizationGroupId,
		line_id: content.line_id,
		sku: content.sku,
		order_by: 'observed_on',
		group_by: ['competitor_name'],
		observed_on__gt: observedAfter,
	}
	const enabled = !!optimizationGroupId

	return useQueries({
		queries: [
			{ ...coreQueries.listPriceHistory(params), enabled },
			{
				...coreQueries.listCompetitiveData({
					...params,
					fields: COMPETITIVE_FIELDS,
				}),
				enabled,
			},
		],
		combine,
	})
}
